import React, { useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Lock, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/auth'), 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-lg bg-violet-600 flex items-center justify-center">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <span className="text-zinc-900 text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            DocuBot AI
          </span>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-8">
          {!token ? (
            <div className="text-center space-y-4" data-testid="reset-password-no-token">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Missing reset token
              </h2>
              <p className="text-zinc-600 text-sm">
                This reset link is missing a required token. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block text-sm text-violet-600 hover:text-violet-700 font-medium"
              >
                Request new link
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-4" data-testid="reset-password-success">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Password updated
              </h2>
              <p className="text-zinc-600 text-sm">Redirecting you to login…</p>
            </div>
          ) : (
            <>
              <div className="mx-auto h-14 w-14 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
                <Lock className="h-7 w-7 text-violet-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900 text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Set a new password
              </h2>
              <p className="text-zinc-600 text-sm text-center mb-6">
                Choose a strong password with at least 6 characters.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="new-pw">New password</Label>
                  <Input
                    id="new-pw"
                    type="password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    className="mt-2"
                    data-testid="reset-password-new-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-pw">Confirm password</Label>
                  <Input
                    id="confirm-pw"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="mt-2"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-zinc-900 hover:bg-zinc-800"
                  disabled={loading}
                  data-testid="reset-password-submit-button"
                >
                  {loading ? 'Updating…' : 'Update password'}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <Link
                  to="/auth"
                  className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
